import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import Markdown from 'react-markdown';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { MarkdownContent, WebsiteContent } from '../../../extViewmodels/sdk/models/Website';
import { useErrorMessages, useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { copyToClipboard } from '../../../models/UiUtils';
import { useAllWebsiteQuery } from '../../../queries/Website/useAllWebsiteQuery';
import { useDeleteWebsiteContentMutation } from '../../../queries/Website/useDeleteWebsiteContentMutation';
import { useEditWebsiteContentParentMutation } from '../../../queries/Website/useEditWebsiteContentParentMutation';
import { useUpdateWebsiteContentMutation } from '../../../queries/Website/useUpdateWebsiteContentMutation';
import { useWebsiteAnonymousURIQuery } from '../../../queries/Website/useWebsiteAnonymousURIQuery';
import {
	getWebsiteContentListQueryKey,
	invalidateWebsiteContentListQuery,
	useWebsiteContentListQuery,
} from '../../../queries/Website/useWebsiteContentListQuery';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../web/components-v2/Dropdown';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../web/components/MultiContainerHeader';
import { GripVerticalIcon } from '../../../web/components/svgs/icons/GripVerticalIcon';
import { LeftChevron } from '../../../web/components/svgs/icons/LeftChevron';
import { StickyNoteIcon } from '../../../web/components/svgs/icons/StickyNoteIcon';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { aceGray, background, brandPrimary, charmGray, shuffleGray, titles } from '../../../web/styles/colors';
import { bs } from '../../../web/styles/styles';
import { CopyMageContentNameDialog } from '../CopyMageAiChat';

const s = StyleSheet.create({
	root: {
		display: 'flex',
	},
	sidebar: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: background,
		height: '100%',
		borderRight: `1px solid ${shuffleGray}`,
		padding: `32px 8px`,
		width: 228,
		boxSizing: 'border-box',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		paddingLeft: 54,
		maxWidth: 686,
	},
	sidebarTitle: {
		color: brandPrimary,
		fontSize: `1rem`,
		marginBottom: 16,
	},
	sidebarMenuItem: {
		display: `flex`,
		flexDirection: `column`,
		gap: 8,
	},
	sidebarMenuItemOver: {
		backgroundColor: `#0466FF30`,
	},
	sidebarMenuItemDroppableOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		pointerEvents: 'none',
		transform: 'none !important',
	},
	sidebarMenuItemInner: {
		boxSizing: `border-box`,
		color: titles,
		backgroundColor: '#9198A407',
		padding: 8,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		borderRadius: 16,
		maxWidth: 208,
		'--gripper': 'none',
		'--sidebar-more-menu-button-opacity': `0`,
		':hover': {
			backgroundColor: '#9198A41A',
			'--gripper': 'flex',
			'--sidebar-more-menu-button-opacity': `1`,
		},
		':has([data-state="open"])': {
			'--sidebar-more-menu-button-opacity': `1`,
		},
	},
	sidebarNestedMenuItemButton: {
		color: charmGray,
	},
	sidebarMenuItemButtonPopover: {
		padding: 0,
	},
	grip: {
		// @ts-ignore
		display: 'var(--gripper)',
	},
	sidebarContainer: {
		display: `flex`,
		flexDirection: `column`,
		gap: 8,
	},
	sidebarItemContainer: {
		position: `relative`,
	},
	sidebarNestedContainer: {
		paddingLeft: 16,
	},
	sidebarMenuItemSelected: {
		color: titles,
		backgroundColor: '#0466FF07',
	},
	sidebarMenuItemIcon: {
		color: charmGray,
		width: 12,
		height: 12,
	},
	sidebarMenuItemIconSelected: {
		color: brandPrimary,
	},
	sidebarMenuItemButton: {
		flex: 1,
	},
	sidebarMenuItemMoreButton: {
		// @ts-ignore
		opacity: `var(--sidebar-more-menu-button-opacity)`,
	},
	contentHeader: {
		boxSizing: 'border-box',
		paddingTop: 24,
		paddingBottom: 16,
	},
	btnBack: {
		color: brandPrimary,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	contentMd: {
		backgroundColor: background,
		borderRadius: 8,
		border: `1px solid ${shuffleGray}`,
		padding: `16px 24px`,
		marginBottom: 24,
		flex: 1,
		fontSize: '0.875rem',
		lineHeight: `30px`,
		overflow: 'auto',
		':has(*) ul,ol': {
			paddingLeft: 16,
			margin: 0,
			listStyle: 'revert',
		},
		':has(*) li': {
			listStyle: 'inherit',
		},
	},
	footerActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 16,
		marginBottom: 44,
	},
	btnShare: {
		flex: 3,
	},
	btnDelete: {
		flex: 1,
	},
	loadingContainer: {
		display: `flex`,
		justifyContent: `center`,
		padding: 16,
	},
	topLevelDroppableWrapper: {
		boxSizing: 'border-box',
		display: `flex`,
		flexDirection: `column`,
		gap: 8,
		paddingBottom: 4,
	},
	topLevelDroppableOver: {
		borderBottom: `2px solid ${brandPrimary}`,
	},
	dragHandle: {
		width: 12,
		height: 12,
		color: aceGray,
	},
});

export function isWebsiteContentMarkdownContent(content: WebsiteContent): content is MarkdownContent {
	return (content as MarkdownContent).body !== undefined;
}

export function nestWebsiteContentList<T extends WebsiteContent>(contentList: T[]) {
	const nestedContentList: { parent: T; children: T[] }[] = contentList
		.filter(c => !c.parentWebsiteContentId)
		.map(c => ({ parent: c, children: [] }));
	for (const content of contentList) {
		if (content.parentWebsiteContentId) {
			nestedContentList.find(({ parent }) => parent.id === content.parentWebsiteContentId).children.push(content);
		}
	}
	return nestedContentList;
}

const TOP_LEVEL_DROPPABLE_ID = 'top-level-droppable-id';

export function CopyMageWireframe() {
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('CopyMageWireframe');
	const [contentToEdit, setContentToEdit] = React.useState<MarkdownContent | null>(null);
	const [contentIdToDelete, setContentIdToDelete] = React.useState<string | null>(null);
	const [selectedContentId, setSelectedContentId] = React.useState<string | null>(null);
	const { accountId } = useParams<{ accountId: string }>();
	const impersonationContext: Api.IImpersonationContext = { account: { id: accountId } };
	const allWebsiteQuery = useAllWebsiteQuery({
		impersonationContext,
	});
	const activeWebsiteId: string | null = allWebsiteQuery.data?.pages?.[0].values?.[0].id ?? null;
	const websiteContentListQuery = useWebsiteContentListQuery({
		enabled: Boolean(activeWebsiteId),
		id: activeWebsiteId,
		impersonationContext,
	});
	const websiteAnonymousURIQuery = useWebsiteAnonymousURIQuery({
		id: activeWebsiteId,
		enabled: Boolean(activeWebsiteId),
		impersonationContext,
	});
	const deleteWebsiteContentMutation = useDeleteWebsiteContentMutation({
		onError: error => {
			setContentIdToDelete(null);
			logApiError('DeleteWebsiteContentMutation', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: (_, params) => {
			setSelectedContentId(null);
			setContentIdToDelete(null);
			invalidateWebsiteContentListQuery({ id: params.websiteId });
		},
	});
	const updateWebsiteContentMutation = useUpdateWebsiteContentMutation({
		onError: error => {
			setContentToEdit(null);
			logApiError('EditWebsiteContentMutation', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: (_, params) => {
			setContentToEdit(null);
			invalidateWebsiteContentListQuery({ id: params.websiteId });
		},
	});
	const handleConfirmDeleteContent = (_: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
		if (cancel) {
			setContentIdToDelete(null);
			return;
		}
		deleteWebsiteContentMutation.mutate({
			websiteId: activeWebsiteId,
			contentId: selectedContentId,
			impersonationContext,
		});
	};
	const handleConfirmEditContent = (title: string) => {
		updateWebsiteContentMutation.mutate({
			impersonationContext,
			websiteId: activeWebsiteId,
			contentId: contentToEdit.id,
			content: {
				...contentToEdit,
				name: title,
			},
		});
		setContentToEdit(null);
	};
	const websiteContentParentMutation = useEditWebsiteContentParentMutation({
		onError: error => {
			logApiError('EditWebsiteContentParentMutation', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			invalidateWebsiteContentListQuery({ id: activeWebsiteId });
		},
	});

	React.useEffect(() => {
		if (websiteContentListQuery.data?.length) {
			const firstMarkdownContent = websiteContentListQuery.data.find(isWebsiteContentMarkdownContent);
			if (!selectedContentId && firstMarkdownContent) {
				setSelectedContentId(firstMarkdownContent.id);
			}
		}
	}, [websiteContentListQuery.data, selectedContentId]);
	if (!websiteContentListQuery.data) {
		return (
			<PageLayout>
				<div className={css(s.loadingContainer)}>
					<LoadingSpinner />
				</div>
			</PageLayout>
		);
	}

	const markdownContentList = websiteContentListQuery.data?.filter(isWebsiteContentMarkdownContent) ?? [];
	if (markdownContentList.length === 0) {
		return (
			<PageLayout>
				<div>You don&apos;t have any saved website content</div>
			</PageLayout>
		);
	}
	const nestedContentList = nestWebsiteContentList<MarkdownContent>(markdownContentList);
	const selectedContent = selectedContentId ? markdownContentList.find(c => c.id === selectedContentId) : null;
	const handleDeleteClick = (contentId: string) => {
		setContentIdToDelete(contentId);
	};
	const handleEditClick = (content: MarkdownContent) => {
		setContentToEdit(content);
	};
	const handleCancelAssistantMessageSave = () => {
		setContentToEdit(null);
	};
	const handleDragEnd = (result: DropResult) => {
		if (result.destination == null || result.draggableId === result.destination.droppableId) {
			return;
		}
		const childContent = markdownContentList.find(c => c.id === result.draggableId);
		// Don't allow moving top-level content to top-level
		if (result.destination.droppableId === TOP_LEVEL_DROPPABLE_ID && childContent.parentWebsiteContentId == null) {
			return;
		}
		// Don't allow moving content to its current parent
		if (result.destination.droppableId === childContent.parentWebsiteContentId) {
			return;
		}
		websiteContentParentMutation.mutate({
			id: activeWebsiteId,
			contentId: result.draggableId,
			parentContentId: result.destination.droppableId === TOP_LEVEL_DROPPABLE_ID ? '' : result.destination.droppableId,
			impersonationContext,
		});
		// Optimistic update for instant dnd feedback
		reactQueryClient.setQueryData(
			getWebsiteContentListQueryKey({ id: activeWebsiteId, impersonationContext }),
			(data: MarkdownContent[]) => {
				return data.map(c => {
					if (c.id === result.draggableId) {
						return {
							...c,
							parentWebsiteContentId:
								result.destination.droppableId === TOP_LEVEL_DROPPABLE_ID ? null : result.destination.droppableId,
						};
					}
					return c;
				});
			}
		);
	};
	const handleShareContent = () => {
		copyToClipboard(websiteAnonymousURIQuery.data);
		toast.success('Website link copied to clipboard');
	};

	return (
		<PageLayout>
			<div className={css(s.sidebar)}>
				<div className={css(s.sidebarTitle)}>Content</div>
				<DragDropContext onDragEnd={handleDragEnd}>
					<div className={css(s.sidebarContainer)}>
						{nestedContentList.map((entry, index) => {
							const content = entry.parent;
							return (
								<div key={content.id} className={css(s.sidebarItemContainer)}>
									<Droppable droppableId={`placeholder-droppable-${content.id}`} key={content.id} isDropDisabled={true}>
										{itemDroppableProvided => {
											const hasChildren = Boolean(entry.children.length);
											return (
												<div
													className={css(s.sidebarMenuItem)}
													ref={itemDroppableProvided.innerRef}
													{...itemDroppableProvided.droppableProps}
												>
													<Draggable
														key={content.id}
														draggableId={content.id}
														isDragDisabled={hasChildren || websiteContentParentMutation.isPending}
														index={index}
													>
														{provided => {
															return (
																<div
																	ref={provided.innerRef}
																	{...provided.draggableProps}
																	className={css(
																		s.sidebarMenuItemInner,
																		bs.truncateText,
																		selectedContentId === content.id && s.sidebarMenuItemSelected
																	)}
																	onClick={() => setSelectedContentId(content.id)}
																>
																	{!hasChildren ? (
																		<span className={css(s.grip)} {...provided.dragHandleProps}>
																			<GripVerticalIcon className={css(s.dragHandle)} />
																		</span>
																	) : null}
																	<StickyNoteIcon
																		className={css(
																			s.sidebarMenuItemIcon,
																			selectedContentId === content.id && s.sidebarMenuItemIconSelected
																		)}
																	/>
																	<button className={css(s.sidebarMenuItemButton)}>{content.name}</button>
																	<Dropdown>
																		<DropdownEllipsisTrigger styles={[s.sidebarMenuItemMoreButton]} />
																		<DropdownContent>
																			<DropdownItem onClick={() => handleEditClick(content)}>Rename</DropdownItem>
																			<DropdownItem onClick={() => handleDeleteClick(content.id)}>Delete</DropdownItem>
																		</DropdownContent>
																	</Dropdown>
																</div>
															);
														}}
													</Draggable>
													{entry.children.map((childContent, i) => {
														return (
															<div key={childContent.id} className={css(s.sidebarNestedContainer)}>
																<Draggable
																	draggableId={childContent.id}
																	index={i}
																	isDragDisabled={websiteContentParentMutation.isPending}
																>
																	{provided => {
																		return (
																			<div
																				ref={provided.innerRef}
																				{...provided.draggableProps}
																				className={css(
																					s.sidebarMenuItemInner,
																					bs.truncateText,
																					selectedContentId === childContent.id && s.sidebarMenuItemSelected
																				)}
																				onClick={() => setSelectedContentId(childContent.id)}
																			>
																				<span className={css(s.grip)} {...provided.dragHandleProps}>
																					<GripVerticalIcon className={css(s.dragHandle)} />
																				</span>
																				<button className={css(s.sidebarMenuItemButton, s.sidebarNestedMenuItemButton)}>
																					{childContent.name}
																				</button>
																				<Dropdown>
																					<DropdownEllipsisTrigger styles={[s.sidebarMenuItemMoreButton]} />
																					<DropdownContent>
																						<DropdownItem onClick={() => handleEditClick(childContent)}>
																							Rename
																						</DropdownItem>
																						<DropdownItem onClick={() => handleDeleteClick(childContent.id)}>
																							Delete
																						</DropdownItem>
																					</DropdownContent>
																				</Dropdown>
																			</div>
																		);
																	}}
																</Draggable>
															</div>
														);
													})}
												</div>
											);
										}}
									</Droppable>
									<Droppable droppableId={content.id}>
										{(itemDroppableProvided, itemDroppableSnapshot) => {
											return (
												<div
													className={css(
														s.sidebarMenuItemDroppableOverlay,
														itemDroppableSnapshot.isDraggingOver && s.sidebarMenuItemOver
													)}
													ref={itemDroppableProvided.innerRef}
													{...itemDroppableProvided.droppableProps}
												/>
											);
										}}
									</Droppable>
								</div>
							);
						})}
					</div>
					<Droppable droppableId={TOP_LEVEL_DROPPABLE_ID}>
						{(droppableProvided, droppableSnapshot) => {
							return (
								<div
									className={css(
										s.topLevelDroppableWrapper,
										droppableSnapshot.isDraggingOver && s.topLevelDroppableOver
									)}
									ref={droppableProvided.innerRef}
								/>
							);
						}}
					</Droppable>
				</DragDropContext>
			</div>
			<div className={css(s.content)}>
				<div className={css(s.contentHeader)}>
					<button className={css(bs.outlineButton, s.btnBack)} onClick={() => fullscreenModal.history.goBack()}>
						<LeftChevron width={7} height={12} colorFill={brandPrimary} />
						<span>Return to CopyMage</span>
					</button>
				</div>
				{selectedContent ? (
					<>
						<div className={css(s.contentMd)}>
							<Markdown>{selectedContent.body}</Markdown>
						</div>
						<div className={css(s.footerActions)}>
							<button
								className={css(bs.ctaButton, s.btnShare)}
								disabled={!websiteAnonymousURIQuery.data}
								onClick={handleShareContent}
							>
								Share Content
							</button>
							<button
								className={css(bs.ctaButtonDestructive, s.btnDelete)}
								onClick={() => handleDeleteClick(selectedContent.id)}
							>
								Delete
							</button>
						</div>
					</>
				) : null}
			</div>
			<CopyMageContentNameDialog
				key={`edit-dialog-${contentToEdit?.id}`}
				isOpen={contentToEdit != null}
				onSave={handleConfirmEditContent}
				onClose={handleCancelAssistantMessageSave}
			/>
			<ConfirmationDialog
				modalProps={{
					isOpen: contentIdToDelete != null,
					onRequestClose: handleConfirmDeleteContent,
				}}
				icon={<WarningIcon />}
				title='Are you sure you want to delete this content?'
				options={DefaultDeleteConfirmationOptions.map(option => ({
					...option,
					disabled: deleteWebsiteContentMutation.isPending,
				}))}
			/>
		</PageLayout>
	);
}

function PageLayout({ children }: { children: React.ReactNode }) {
	const fullscreenModal = useFullscreenModal();
	return (
		<div className={css(s.root)}>
			<MultiContainerHeader
				fullscreenHeader='Company Name'
				onFullscreenRequestBack={ev => {
					ev.preventDefault();
					fullscreenModal.dismissModal();
				}}
				onFullscreenRequestClose={() => fullscreenModal.dismissModal()}
			/>
			{children}
		</div>
	);
}
